import React, { useEffect } from "react"
import { FeatureGate } from "../../../../utils/FeatureGate"
import FinancialBenefitsNav from "./FinancialBenefitsNav"
import { Container, Stack, useBreakpointValue } from "@carrotfertility/carotene"
import { HelpCenterNav } from "./HelpCenterNav"
import { NavItemAndIcon } from "./NavItemAndIcon"
import { DrawerBackground, DrawerContainer, DrawerNonActiveBackground, XButton } from "./NavDrawer"
import { useSelector } from "react-redux"
import { getCarrotLite } from "../../../../reducers/companyInfo"
import useGetThreads from "../../../messaging/hooks/useGetThreads"
import { useIntl } from "react-intl"
import useBenefit from "components/dynamic-content/hooks/useGetBenefit"

interface NavMenuProps {
  collapsed: boolean
  collapseMenu: () => void
  appLockedDown: boolean
}

function NavigationMenu({ collapsed, collapseMenu, appLockedDown }: NavMenuProps): JSX.Element {
  const active = collapsed ? "" : "active"
  const isMobileView = useBreakpointValue<boolean>([true, true, false, false])
  const carrotLite = useSelector(getCarrotLite)
  const { data: messageThreads } = useGetThreads()
  const unread = messageThreads?.threads?.some((thread) => thread.hasUnreadMessages)
  const intl = useIntl()
  const benefit = useBenefit()

  useEffect(() => {
    if (!collapsed && isMobileView) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "visible"
    }
  }, [collapsed, active, isMobileView])

  const navItemsToShow = [
    {
      navItem: (
        <Container height={"unset"} stack={"large"}>
          <NavItemAndIcon
            exact
            onClick={collapseMenu}
            icon={"icn-your-carrot-plan"}
            id="nav-home-link"
            link={"/"}
            text={intl.formatMessage({
              defaultMessage: "Your Carrot Plan"
            })}
          />
        </Container>
      ),
      key: "nav-home-link",
      showForAppLockedDown: true,
      showForCarrotLite: true
    },
    {
      navItem: (
        <NavItemAndIcon
          icon={"icn-messages-plane"}
          id="nav-messages-link"
          link={"/messages"}
          text="Messages"
          onClick={collapseMenu}
          showUnreadIndicator={unread}
        />
      ),
      key: "nav-messages-link",
      showForAppLockedDown: true,
      showForFeatureToggle: true,
      showForCarrotLite: true
    },
    {
      navItem: (
        <NavItemAndIcon
          icon={"icn-read-learn-lightbulb"}
          id="nav-library-link"
          link={"/read"}
          text="Read & learn"
          onClick={collapseMenu}
        />
      ),
      key: "nav-library-link",
      showForAppLockedDown: true,
      showForCarrotLite: true
    },
    {
      navItem: <FinancialBenefitsNav onClick={collapseMenu} />,
      key: "nav-transactions-link",
      showForAppLockedDown: false,
      showForCarrotLite: false
    },
    {
      navItem: (
        <NavItemAndIcon
          icon={"icn-talk-to-expert"}
          id="nav-ttc-link"
          link={"/talk-to-carrot"}
          text="Talk to Carrot"
          onClick={collapseMenu}
        />
      ),
      key: "nav-ttc-link",
      showForAppLockedDown: false,
      showForCarrotLite: true
    },
    {
      navItem: (
        <NavItemAndIcon
          icon={"icn-eligible-providers"}
          id="nav-provider-finder-link"
          link={"/providerfinder"}
          text="Find a provider"
          onClick={collapseMenu}
        />
      ),
      key: "nav-provider-finder-link",
      showForAppLockedDown: false,
      showForCarrotLite: true,
      isHidden: benefit.isLoading || !benefit.data?.providerFinder.showProviderFinder
    },
    {
      navItem: (
        <NavItemAndIcon
          icon={"icn-benefits-guide"}
          id="nav-your-benefit-guide-link"
          link="/your-benefit-guide"
          text="Your benefit guide"
          onClick={collapseMenu}
        />
      ),
      key: "nav-your-benefit-guide-link",
      showForAppLockedDown: true,
      showForCarrotLite: true
    },
    {
      navItem: (
        <>
          <FeatureGate filter={(c) => c.isSupportCenterEnabled}>
            <HelpCenterNav />
          </FeatureGate>
        </>
      ),
      key: "nav-support-center-link",
      showForAppLockedDown: true,
      showForCarrotLite: true
    }
  ]
    .filter((ni) => (appLockedDown && ni.showForAppLockedDown) || !appLockedDown)
    .filter((ni) => ni.showForFeatureToggle == undefined || !!ni.showForFeatureToggle)
    .filter((ni) => (carrotLite && ni.showForCarrotLite) || !carrotLite)
    .filter(({ isHidden }) => !isHidden)

  return (
    <>
      <DrawerNonActiveBackground active={active} onClick={collapseMenu} />
      <DrawerContainer active={active}>
        <DrawerBackground>
          <XButton onClick={collapseMenu} />
          <Container height={"unset"} stack={isMobileView ? "giant" : "large"} />
          <Stack spacing={"32px"} height={"auto"}>
            {navItemsToShow.map((ni) => (
              <React.Fragment key={ni.key}>{ni.navItem}</React.Fragment>
            ))}
          </Stack>
        </DrawerBackground>
      </DrawerContainer>
    </>
  )
}

export default NavigationMenu
